import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../Styles/IndividualUserProfile.css'; // Assuming you have a CSS file for styles

const BASE_URL = process.env.REACT_APP_API_URL;

const JBranchUserProfile = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [activeTab, setActiveTab] = useState("profileDetails"); // Default active tab
  // Add state to track existing profile image path
  const [existingImagePath, setExistingImagePath] = useState(null);
  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    profileImage: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');

  const userData = JSON.parse(localStorage.getItem("user"));
  const userId = userData?.id;
  const userRole = userData?.role; 
  const userBranchCode = userData?.BranchCode;
  const userName = `${userData?.firstName} ${userData?.lastName}`;
  const userEmail = user.emailAddress;

  // Add toast notification helper
  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  // Fetch user data when the component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId || isNaN(userId) || userId <= 0) {
        notify("Invalid user ID provided.", "error");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/get-user-data/${userId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }
        const data = await response.json();
        setUser(data);
        if (data.profileImage) {
          const imageUrl = `${BASE_URL}${data.profileImage}`;
          setPreviewImage(imageUrl);
          setExistingImagePath(data.profileImage); // Store path for later deletion
        }
      } catch (error) {
        console.error("Error loading user data:", error);
        notify("Failed to load user data.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value }); // Update specific field in user state
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!user.firstName || !user.lastName) {
      notify("First name and last name are required.", "error");
      return;
    }
  
    try {
      // If a new profile image is being uploaded and there's an existing one, delete the old one
      if (profileImage && existingImagePath) {
        try {
          // Call API to delete the old profile image
          const deleteResponse = await fetch(`${BASE_URL}/delete-profile-image`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: userId,
              imagePath: existingImagePath
            })
          });
          
          if (!deleteResponse.ok) {
            console.warn('Failed to delete old profile image, but continuing with update');
          } 
        } catch (deleteError) {
          console.warn('Error deleting old profile image:', deleteError);
          // Don't block the update if deletion fails
        }
      }
  
      const formData = new FormData();
      formData.append("firstName", user.firstName);
      formData.append("lastName", user.lastName);
      formData.append("phoneNumber", user.phoneNumber);
      if (profileImage) {
        formData.append("profileImage", profileImage);
      }
  
      const response = await fetch(
        `${BASE_URL}/update-user-data/${userId}`,
        {
          method: 'PUT',
          body: formData
        }
      );
  
      if (!response.ok) {
        throw new Error('Failed to update profile');
      }
  
      const data = await response.json();
      
      // Update local state with new data
      setUser(prev => ({
        ...prev,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        profileImage: data.profileImage
      }));
      
      // Update localStorage with complete updated data
      const updatedUserData = {
        ...userData,
        firstName: data.firstName || user.firstName,
        lastName: data.lastName || user.lastName,
        phoneNumber: data.phoneNumber || user.phoneNumber,
        profileImage: data.profileImage || existingImagePath
      };
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      
      // Update existing image path if a new one was uploaded
      if (data.profileImage) {
        setExistingImagePath(data.profileImage);
        // Update the preview image with the full URL
        setPreviewImage(`${BASE_URL}${data.profileImage}`);
      }
      
      // Reset file input
      setProfileImage(null);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }
      
      notify("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      notify("Error updating profile. Please try again.", "error");
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordError(''); // Clear any previous errors
  };

  const handlePasswordUpdate = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    // Basic validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      notify('All fields are required', "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      notify('New passwords do not match', "error");
      return;
    }

    if (newPassword.length < 6) {
      notify('New password must be at least 6 characters long', "error");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/change-password/${userId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(passwordData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update password');
      }

      notify('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordError('');
    } catch (error) {
      notify(error.message || 'Failed to update password', "error");
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="container-fluid py-3">
      <ToastContainer />
      <div className="container">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading user profile</p>
            <p className="text-muted">Please wait while we fetch user profile data...</p>
          </div>
        ) : (
          <div className="row g-4">
            {/* Left Profile Section */}
            <div className="col-12 col-lg-4 mb-4 mb-lg-0">
              <div className="card text-center h-100 align-items-center">
                <div className="card-body d-flex flex-column align-items-center">
                  <div className="position-relative mx-auto mb-4 ">
                    <img
                      src={previewImage || "https://www.w3schools.com/w3images/avatar2.png"}
                      alt="Profile"
                      onError={(e) => { e.target.src = "https://www.w3schools.com/w3images/avatar2.png"; }}
                      className="rounded-circle profile-image"
                      style={{ width: "120px", height: "120px", objectFit: "cover" }}
                    />
                  </div>
                  <h6 className="mb-2">{userRole}</h6>
                  <h5 className="mb-2">{user.firstName} {user.lastName}</h5>
                  
                  <p className="text-muted ">{user.emailAddress}</p>
                
                  {/* <button className="btn btn-warning">Deactivate account</button> */}
                </div>
              </div>
            </div>

            {/* Right Tabs Section */}
            <div className="col-12 col-lg-8">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="nav nav-pills flex-wrap" role="tablist">
                      <button
                        className={`nav-link ${activeTab === "profileDetails" ? "active" : ""}`}
                        onClick={() => handleTabClick("profileDetails")}
                      >
                        Profile details
                      </button>
                      <button
                        className={`nav-link ${activeTab === "changePassword" ? "active" : ""}`}
                        onClick={() => handleTabClick("changePassword")}
                      >
                        Change Password
                      </button>
                    </div>
                    <div className="d-flex gap-2">
                      {activeTab === "profileDetails" && (
                        <button className="btn btn-primary" onClick={handleSave}>Save</button>
                      )}
                      {/* <button className="btn btn-warning">Upgrade</button> */}
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {activeTab === "profileDetails" && (
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={user.firstName}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={user.lastName}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Phone Number</label>
                        <input
                          type="text"
                          name="phoneNumber"
                          value={user.phoneNumber}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Email Address</label>
                        <input
                          type="email"
                          name="emailAddress"
                          value={user.emailAddress}
                          className="form-control"
                          disabled
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Profile Picture</label>
                        <input 
                        id="profile-image-input"
                          type="file" 
                          className="form-control" 
                          onChange={handleImageChange}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === "changePassword" && (
                    <div className="row g-3">
                      {passwordError && (
                        <div className="col-12">
                          <div className="alert alert-danger">{passwordError}</div>
                        </div>
                      )}
                      <div className="col-12">
                        <label className="form-label">Current Password</label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">New Password</label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-warning"
                          onClick={handlePasswordUpdate}
                        >
                          Update Password
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JBranchUserProfile;
